import cn from 'classnames'
import { useState, useCallback, useRef } from 'react'

import { Button, IconPauseDefault, IconPlayDefault } from '@mc/design-system'
import Experiment from '@mc/experiments-react'
import { useTranslation } from '@mc/i18n'

import {
  CAROUSEL_SPRINGBOARD_EXP_FLAG,
  DoubleColumnSpringboardCarousel,
  HorizontalSpringboardCarousel,
  SingleColumnSpringboardCarousel,
  useCarouselSpringboardExp,
} from 'experiments/pen-5453_v1_hp_carousel_to_quiz'

import { DoubleColumnCarousel } from './DoubleColumnCarousel'
import { SingleColumnCarousel } from './SingleColumnCarousel'
import { CarouselResponsiveLayout } from './CarouselResponsiveLayout'
import { actions, trackClickCarouselHero, trackPlayPauseClick } from './events'
import { useCarouselImages, useCarouselScrollDuration } from './hooks'

import styles from './Carousel.module.scss'

export const Carousel = () => {
  const { t } = useTranslation('@mc/next')
  const { singleImages, doubleImages } = useCarouselImages()
  const { inPen5453Experiment, isPen5453Variant } = useCarouselSpringboardExp()
  const doubleImageScrollDuration = useCarouselScrollDuration(
    doubleImages.length,
  )
  const singleImageScrollDuration = useCarouselScrollDuration(
    singleImages.length,
  )

  const isPlayPauseButtonClickedRef = useRef(false)
  // pen-5453_v1 additional hover handler state to not override user's button animation choice with hovering:
  const [isAnimationActive, setIsAnimationActive] = useState(true)

  const toggleCarouselAnimation = useCallback(() => {
    trackPlayPauseClick(
      isAnimationActive
        ? actions.PAUSE_CAROUSEL_ANIMATION
        : actions.RESUME_CAROUSEL_ANIMATION,
    )
    setIsAnimationActive(!isAnimationActive)
    isPlayPauseButtonClickedRef.current = !isPlayPauseButtonClickedRef.current
  }, [isAnimationActive])

  const handleHoverChange = useCallback(
    (hovering?: boolean) => {
      if (isPen5453Variant && !isPlayPauseButtonClickedRef.current) {
        setIsAnimationActive(!hovering)
      }
    },
    [isPen5453Variant, setIsAnimationActive],
  )

  const renderDefaultCarousel = useCallback(
    () => (
      <CarouselResponsiveLayout
        mobile={<DoubleColumnCarousel size='sm' images={doubleImages} />}
        tablet={<SingleColumnCarousel size='md' images={singleImages} />}
        desktop={<DoubleColumnCarousel size='lg' images={doubleImages} />}
      />
    ),
    [doubleImages, singleImages],
  )

  const renderSpringboardVariant = useCallback(
    () => (
      <CarouselResponsiveLayout
        mobile={<HorizontalSpringboardCarousel />}
        tablet={<SingleColumnSpringboardCarousel />}
        desktop={<DoubleColumnSpringboardCarousel />}
      />
    ),
    [],
  )

  return (
    <div
      className={cn(
        'mc-overflow--hidden mc-h-100',
        styles.carouselContainerGradient,
        { [styles.noMobileGradient]: isPen5453Variant },
      )}
    >
      {/* We are adding the onClick because we want to fire an event
        but we don't want to show it as a button to the user */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        id='hero-carousel'
        className={cn('d-flex flex-column', styles.imageLoopContent, {
          [styles.pen5453Experiment]: inPen5453Experiment,
          [styles.pen5453Variant]: isPen5453Variant,
        })}
        style={{
          animationPlayState: isAnimationActive ? 'running' : 'paused',
          ['--double-image-scroll-duration' as string]: `${doubleImageScrollDuration}s`,
          ['--single-image-scroll-duration' as string]: `${singleImageScrollDuration}s`,
        }}
        onClick={() => {
          if (!isPen5453Variant) {
            trackClickCarouselHero(actions.CLICK_CAROUSEL_HERO)
          }
        }}
        onMouseEnter={() => handleHoverChange(true)}
        onMouseLeave={() => handleHoverChange(false)}
      >
        <Experiment
          name={CAROUSEL_SPRINGBOARD_EXP_FLAG}
          default={renderDefaultCarousel}
          variant_1={renderSpringboardVariant}
          variant_2={renderSpringboardVariant}
          track={false}
        />
      </div>
      <Button
        kind='tertiary'
        size='small'
        className='mc-corners--circle mc-m-2 mc-p-2'
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          zIndex: 2,
        }}
        onClick={toggleCarouselAnimation}
        aria-label={
          isAnimationActive
            ? t('home.hero.a11y.pauseAriaLabel')
            : t('home.hero.a11y.playAriaLabel')
        }
      >
        {isAnimationActive ? (
          <IconPauseDefault size='lg' />
        ) : (
          <IconPlayDefault size='lg' />
        )}
      </Button>
    </div>
  )
}
